import * as React from 'react';
import LayoutDashboard from 'components/Layout/Dashboard';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Breadcrumb from 'components/Breadcrumbs/Breadcrumb';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { connectWithRouter } from 'helpers/connectWithRouter';
import { bindActionCreators, Dispatch } from 'redux';
import { SyncHotelPayload, syncHotel } from '../../actions/ManageTB/Hotels/Sync';
import { lookupHotelById, LookupHotelByIdPayload, LookupHotelByIdResponse, HotelMappingEntry } from '../../actions/ManageTB/Hotels/GetSingle';

const styles = require('./styles.pcss');

type Props = {
  syncHotel: (data: SyncHotelPayload, onError: (message: string) => void, onSettled: () => void) => void;
  lookupHotelById: (
    data: LookupHotelByIdPayload,
    onSuccess: (result: LookupHotelByIdResponse) => void,
    onError: (message: string) => void,
    onSettled: () => void,
  ) => void;
};

type State = {
  expediaId: string;
  travelportId: string;
  apiError: string | null;
  isLoading: boolean;
  lookupRapidId: string;
  lookupTravelportId: string;
  lookupError: string | null;
  lookupLoading: boolean;
  lookupResult: LookupHotelByIdResponse | null;
  copiedPanel: string | null;
  expandedEntries: Record<string, boolean>;
};

export class ManageTBSyncHotels extends React.Component<Props, State> {
  escapeHtml = (value: string) => {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  formatJsonForDisplay = (value: unknown) => {
    const serialized = JSON.stringify(value, null, 2);
    const escaped = this.escapeHtml(serialized);

    return escaped.replace(
      /(\"(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\\"])*\"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (token: string) => {
        let tokenClass = 'jsonNumber';

        if (/^\"/.test(token)) {
          tokenClass = /:$/.test(token) ? 'jsonKey' : 'jsonString';
        } else if (/true|false/.test(token)) {
          tokenClass = 'jsonBoolean';
        } else if (/null/.test(token)) {
          tokenClass = 'jsonNull';
        }

        return `<span class="${tokenClass}">${token}</span>`;
      },
    );
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      expediaId: '',
      travelportId: '',
      apiError: null,
      isLoading: false,
      lookupRapidId: '',
      lookupTravelportId: '',
      lookupError: null,
      lookupLoading: false,
      lookupResult: null,
      copiedPanel: null,
      expandedEntries: {},
    };
  }

  copyJsonToClipboard = async (panelKey: string, value: unknown) => {
    if (!value || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      this.setState({ copiedPanel: panelKey });

      window.setTimeout(() => {
        this.setState((prevState) =>
          prevState.copiedPanel === panelKey ? { copiedPanel: null } : null,
        );
      }, 1200);
    } catch (error) {
      this.setState({ copiedPanel: null });
    }
  };

  handleExpediaIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ expediaId: event.target.value, apiError: null });
  };

  handleTravelportIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ travelportId: event.target.value, apiError: null });
  };

  handleLookupRapidIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ lookupRapidId: event.target.value, lookupError: null });
  };

  handleLookupTravelportIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ lookupTravelportId: event.target.value, lookupError: null });
  };

  toggleEntryExpanded = (entryKey: string) => {
    this.setState((prevState) => ({
      expandedEntries: { ...prevState.expandedEntries, [entryKey]: !prevState.expandedEntries[entryKey] },
    }));
  };

  handleSync = () => {
    const expediaId = this.state.expediaId.trim();
    const travelportId = this.state.travelportId.trim();

    if (!expediaId && !travelportId) {
      this.setState({ apiError: null, isLoading: false });
      return;
    }

    if (expediaId && travelportId) {
      this.setState({ apiError: null, isLoading: false });
      return;
    }

    this.setState({ apiError: null, isLoading: true });
    const payload: { expediaId?: string; travelportId?: string } = {};
    if (expediaId) payload.expediaId = expediaId;
    if (travelportId) payload.travelportId = travelportId;
    this.props.syncHotel(
      payload,
      (message: string) => this.setState({ apiError: message }),
      () => this.setState({ isLoading: false }),
    );
  };

  handleSearch = () => {
    const lookupRapidId = this.state.lookupRapidId.trim();
    const lookupTravelportId = this.state.lookupTravelportId.trim();

    if (!lookupRapidId && !lookupTravelportId) {
      this.setState({ lookupError: null, lookupLoading: false });
      return;
    }

    if (lookupRapidId && lookupTravelportId) {
      this.setState({ lookupError: null, lookupLoading: false });
      return;
    }

    this.setState({ lookupError: null, lookupLoading: true, lookupResult: null, expandedEntries: {} });
    const payload: LookupHotelByIdPayload = {};
    if (lookupRapidId) payload.rapidId = lookupRapidId;
    if (lookupTravelportId) payload.travelportId = lookupTravelportId;
    this.props.lookupHotelById(
      payload,
      (lookupResult: LookupHotelByIdResponse) => this.setState({ lookupResult }),
      (message: string) => this.setState({ lookupError: message }),
      () => this.setState({ lookupLoading: false }),
    );
  };

  renderJsonBlock = (value: unknown) => {
    if (!value) {
      return <Typography className={styles.placeholder}>No data available.</Typography>;
    }

    return (
      <pre className={styles.jsonBlock}>
        <code
          className={styles.jsonCode}
          dangerouslySetInnerHTML={{ __html: this.formatJsonForDisplay(value) }}
        />
      </pre>
    );
  };

  formatPreviewValue = (value: unknown): string => {
    if (typeof value === 'string') {
      return value.length > 20 ? `"${value.slice(0, 20)}…"` : `"${value}"`;
    }
    if (value === null) {
      return 'null';
    }
    if (typeof value === 'object') {
      return Array.isArray(value) ? '[...]' : '{...}';
    }
    return String(value);
  };

  getEntryPreview = (result: Record<string, unknown>) => {
    const keys = Object.keys(result);
    const previewKeys = keys.slice(0, 3);
    const preview = previewKeys.reduce<Record<string, unknown>>((value, key) => {
      value[key] = result[key];
      return value;
    }, {});

    return this.formatJsonForDisplay(preview);
  };

  orderMapping = (value: unknown): unknown => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    const record = value as Record<string, unknown>;
    const priorityKeys = ['travelportId', 'rapidId','giataId'];
    const orderedKeys = [...priorityKeys.filter((key) => key in record), ...Object.keys(record).filter((key) => !priorityKeys.includes(key))];

    return orderedKeys.reduce<Record<string, unknown>>((ordered, key) => {
      ordered[key] = record[key];
      return ordered;
    }, {});
  };

  renderHotelEntries = (sectionKey: string, entries: HotelMappingEntry[] | null | undefined) => {
    if (!entries || !entries.length) {
      return <Typography className={styles.placeholder}>No data available.</Typography>;
    }

    const { expandedEntries } = this.state;

    return (
      <div className={styles.hotelEntries}>
        {entries.map((entry, index) => {
          const entryKey = `${sectionKey}-${entry?.hotelId ?? index}`;
          const isExpanded = Boolean(expandedEntries[entryKey]);
          const rawResult =
            entry?.result ?? (typeof entry?.message === 'string' ? { message: entry.message } : null);
          const result = rawResult && typeof rawResult === 'object' ? (rawResult as Record<string, unknown>) : null;
          const resultKeys = result ? Object.keys(result) : [];
          const isNoDataMessage = resultKeys.length === 1 && resultKeys[0] === 'message';
          const isEmpty = !result || !resultKeys.length;
          const canExpand = !isEmpty && !isNoDataMessage;
          const previewText = isNoDataMessage
            ? String((result as Record<string, unknown>).message)
            : isEmpty
              ? 'No data found'
              : null;

          return (
            <div
              key={entryKey}
              className={styles.hotelEntry}
            >
              <div className={styles.hotelEntryHeader}>
                <Typography className={styles.hotelEntryTitle}>{entry?.hotelId ?? `${index + 1}`}:</Typography>
                {previewText !== null ? (
                  <span className={styles.hotelEntryPreview}>{previewText}</span>
                ) : (
                  <span
                    className={styles.hotelEntryPreview}
                    dangerouslySetInnerHTML={{
                      __html: this.getEntryPreview(result!),
                    }}
                  />
                )}
                {canExpand && (
                  <IconButton
                    size="small"
                    className={`${styles.expandButton} ${isExpanded ? styles.expandButtonOpen : ''}`}
                    onClick={() => this.toggleEntryExpanded(entryKey)}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
              {canExpand && isExpanded && this.renderJsonBlock(entry?.result)}
            </div>
          );
        })}
      </div>
    );
  };

  renderMapping = (mapping: unknown) => {
    const result = mapping && typeof mapping === 'object' && !Array.isArray(mapping)
      ? (mapping as Record<string, unknown>)
      : null;
    const hasInfo = Boolean(result && Object.keys(result).length);
    const isNoDataMessage = Boolean(
      result && Object.keys(result).length === 1 && Object.keys(result)[0] === 'message',
    );
    const isExpanded = Boolean(this.state.expandedEntries.mapping);

    if (!hasInfo) {
      return <Typography className={styles.placeholder}>No data available.</Typography>;
    }

    if (isNoDataMessage) {
      return (
        <div className={styles.hotelEntries}>
          <div className={styles.hotelEntry}>
            <div className={styles.hotelEntryHeader}>
              <span className={styles.hotelEntryPreview}>{String(result!.message)}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.hotelEntries}>
        <div className={styles.hotelEntry}>
          <div className={styles.hotelEntryHeader}>
            <span
              className={styles.hotelEntryPreview}
              dangerouslySetInnerHTML={{ __html: this.getEntryPreview(result!) }}
            />
            <IconButton
              size="small"
              className={`${styles.expandButton} ${isExpanded ? styles.expandButtonOpen : ''}`}
              onClick={() => this.toggleEntryExpanded('mapping')}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </div>
          {isExpanded && this.renderJsonBlock(result)}
        </div>
      </div>
    );
  };

  render() {
    const {
      expediaId,
      travelportId,
      apiError,
      isLoading,
      lookupRapidId,
      lookupTravelportId,
      lookupError,
      lookupLoading,
      lookupResult,
      copiedPanel,
    } = this.state;
    const hasExpediaId = Boolean(expediaId.trim());
    const hasTravelportId = Boolean(travelportId.trim());
    const hasBothIds = hasExpediaId && hasTravelportId;
    const isDisabled = (!hasExpediaId && !hasTravelportId) || isLoading || hasBothIds;
    const hasLookupRapidId = Boolean(lookupRapidId.trim());
    const hasLookupTravelportId = Boolean(lookupTravelportId.trim());
    const hasBothLookupIds = hasLookupRapidId && hasLookupTravelportId;
    const searchDisabled = (!hasLookupRapidId && !hasLookupTravelportId) || lookupLoading || hasBothLookupIds;
    const mappingDisplay = this.orderMapping(lookupResult?.mapping ?? null);

    return (
      <LayoutDashboard>
        <Breadcrumbs className={styles.breadcrumbs}>
          <Breadcrumb
            name="Manage TB"
            arrow
          />
          <Breadcrumb name="Sync Hotels" />
        </Breadcrumbs>
        <div className={styles.header}>
          <h1 className={styles.h1}>Sync Hotels</h1>
        </div>
        <div className={styles.body}>
          <div className={`${styles.half} ${styles.leftHalf}`}>
            <div className={styles.sectionCard}>
              <Typography className={styles.formSubtitle}>Sync Hotel Section</Typography>
              <Alert
                severity="info"
                className={styles.info}
              >
                One ID at a time. This schedules a sync job and may take a few minutes. Use Search Section below to check the results.
              </Alert>
              <div className={styles.inputs}>
                <TextField
                  label="Expedia ID"
                  size="small"
                  value={expediaId}
                  onChange={this.handleExpediaIdChange}
                  disabled={hasTravelportId || isLoading}
                  className={styles.input}
                />
                <div className={styles.or}>or</div>
                <TextField
                  label="Travelport ID"
                  size="small"
                  value={travelportId}
                  onChange={this.handleTravelportIdChange}
                  disabled={hasExpediaId || isLoading}
                  className={styles.input}
                />
              </div>
              {hasBothIds && (
                <Alert
                  severity="error"
                  className={styles.error}
                >
                  Enter only one hotel ID at a time.
                </Alert>
              )}
              <div className={styles.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isDisabled}
                  onClick={this.handleSync}
                  className={styles.button}
                >
                  Sync Hotel
                </Button>
                {isLoading && <CircularProgress size={24} />}
                {!isLoading && apiError && (
                  <Alert
                    severity="error"
                    className={styles.actionError}
                  >
                    {apiError}
                  </Alert>
                )}
              </div>
            </div>

            <div className={`${styles.lookupForm} ${styles.sectionCard}`}>
              <Typography className={styles.formSubtitle}>Search Section</Typography>
              <Alert
                severity="info"
                className={styles.info}
              >
                Searches internal records only. No provider calls.
              </Alert>
              <div className={styles.inputs}>
                <TextField
                  label="Rapid ID"
                  size="small"
                  value={lookupRapidId}
                  onChange={this.handleLookupRapidIdChange}
                  disabled={hasLookupTravelportId || lookupLoading}
                  className={styles.input}
                />
                <div className={styles.or}>or</div>
                <TextField
                  label="Travelport ID"
                  size="small"
                  value={lookupTravelportId}
                  onChange={this.handleLookupTravelportIdChange}
                  disabled={hasLookupRapidId || lookupLoading}
                  className={styles.input}
                />
              </div>
              {hasBothLookupIds && (
                <Alert
                  severity="error"
                  className={styles.error}
                >
                  Enter only one hotel ID at a time.
                </Alert>
              )}
              <div className={styles.lookupActions}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={searchDisabled}
                  onClick={this.handleSearch}
                  className={styles.button}
                >
                  Search
                </Button>
                {lookupLoading && <CircularProgress size={24} />}
                {!lookupLoading && lookupError && (
                  <Alert
                    severity="error"
                    className={styles.lookupActionError}
                  >
                    {lookupError}
                  </Alert>
                )}
              </div>
            </div>
          </div>
          <Divider
            orientation="vertical"
            flexItem
            className={styles.divider}
          />
          <div className={`${styles.half} ${styles.rightHalf}`}>
            <div className={styles.resultsShell}>
              <Typography className={styles.resultsSubtitle}>Internal Search Results</Typography>
              <div className={styles.resultSection}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>Hotel Mapping</h3>
                  <Tooltip title={copiedPanel === 'mapping' ? 'Copied' : 'Copy JSON'}>
                    <span>
                      <IconButton
                        size="small"
                        className={styles.copyButton}
                        onClick={() => this.copyJsonToClipboard('mapping', mappingDisplay)}
                        disabled={!mappingDisplay}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
                <div className={styles.scrollSquare}>{this.renderMapping(mappingDisplay)}</div>
              </div>

              <div className={styles.resultSection}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>Expedia Info</h3>
                  <Tooltip title={copiedPanel === 'expedia' ? 'Copied' : 'Copy JSON'}>
                    <span>
                      <IconButton
                        size="small"
                        className={styles.copyButton}
                        onClick={() => this.copyJsonToClipboard('expedia', lookupResult?.expediaInfo)}
                        disabled={!lookupResult?.expediaInfo}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
                <div className={styles.scrollSquare}>{this.renderHotelEntries('expedia', lookupResult?.expediaInfo)}</div>
              </div>

              <div className={styles.resultSection}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>Travelport Info</h3>
                  <Tooltip title={copiedPanel === 'travelport' ? 'Copied' : 'Copy JSON'}>
                    <span>
                      <IconButton
                        size="small"
                        className={styles.copyButton}
                        onClick={() => this.copyJsonToClipboard('travelport', lookupResult?.travelportInfo)}
                        disabled={!lookupResult?.travelportInfo}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
                <div className={styles.scrollSquare}>{this.renderHotelEntries('travelport', lookupResult?.travelportInfo)}</div>
              </div>
            </div>
          </div>
        </div>
      </LayoutDashboard>
    );
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators({ syncHotel, lookupHotelById }, dispatch);
}

export default connectWithRouter(null, mapDispatchToProps)(ManageTBSyncHotels);
